import React, { useState, useRef, useEffect } from 'react';

// Rounds to the nearest multiple of `step` — e.g. roundToStep(437, 100) = 400.
// Values here are always in the field's raw unit (bps, cents, etc.), never
// the display unit (%, $) — displayFormat/inputFormat handle that
// conversion separately, purely for rendering/typing.
const roundToStep = (value: number, step: number) => Math.round(value / step) * step;

interface RoundingOptions {
  step?: number;
  integer?: boolean;
  decimal?: boolean;
}

// Single source of truth for "what granularity is this slider configured
// to move in" — used by every interaction path (drag, track click, arrow
// key, typed value) so a given slider steps by the same amount no matter
// how it's operated. Previously each call site duplicated this if/else
// chain, and one of them (Handle's drag math) had drifted to round decimal
// values to the nearest 1/20 while every other site used 1/100 — harmless
// today since a drag-computed 1/20 multiple survives a later 1/100 re-round
// unchanged, but a landmine for the next person who touches either number.
const roundValue = (value: number, { step, integer, decimal }: RoundingOptions) => {
  if (step) return roundToStep(value, step);
  if (integer) return Math.round(value);
  if (decimal) return Math.round(value * 100) / 100;
  return Math.round(value * 4) / 4;
};

// Position along the track as a percentage. FilterInputs feeds this slider
// live per-field bounds, and a homogeneous field (every matching loan at the
// same FICO score, say) makes min === max — (value - min) / (max - min) is
// then 0/0, and the resulting NaN breaks the handle's CSS `left` and the
// fill bar's width (both vanish or misplace; the old rc-slider tolerated
// this, this one didn't). There's no real range to represent in that case,
// so pin to 0% instead of dividing by zero.
const toPercent = (value: number, min: number, max: number) =>
  max === min ? 0 : ((value - min) / (max - min)) * 100;

interface HandleProps {
  value: number;
  onPreview: (newValue: number) => void;
  onCommit: () => void;
  min: number;
  max: number;
  trackRef: React.RefObject<HTMLDivElement>;
  step?: number;
  integer?: boolean;
  decimal?: boolean;
}

const Handle: React.FC<HandleProps> = ({
  value,
  min,
  max,
  onPreview,
  onCommit,
  trackRef,
  step,
  integer,
  decimal,
}) => {
  const calculateHandlePosition = (value: number) => `calc(${toPercent(value, min, max)}% - 9px)`;

  const position = calculateHandlePosition(value);

  const handleMouseMove = (e: globalThis.MouseEvent) => {
    if (trackRef.current) {
      const rect = trackRef.current.getBoundingClientRect();
      const newValue = ((e.clientX - rect.left) / rect.width) * (max - min) + min;
      onPreview(roundValue(newValue, { step, integer, decimal }));
    }
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    onCommit();
  };

  const handleMouseDown = () => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const stepBy = (delta: number) => {
    onPreview(value + delta);
    onCommit();
  };

  return (
    <div
      className="absolute top-1/2 z-[2] h-[18px] w-[18px] -translate-y-1/2 cursor-pointer rounded-full border border-brand-500 bg-white shadow-md outline-none transition-shadow hover:ring-4 hover:ring-brand-500/30 focus-visible:ring-4 focus-visible:ring-brand-500/40"
      style={{ left: position }}
      onMouseDown={handleMouseDown}
      tabIndex={0}
      onKeyDown={(e) => {
        const magnitude = step ?? (integer ? 1 : decimal ? 0.05 : 0.25);
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          stepBy(magnitude);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          stepBy(-magnitude);
        }
      }}
    />
  );
};

export interface RangeSliderProps {
  min: number;
  max: number;
  defaultValue: [number, number];
  onChange: (values: [number, number]) => void;
  displayFormat?: (value: number) => string;
  inputFormat?: (value: string) => number;
  displayPercentage?: boolean;
  // Rounds every value (drag, arrow-key, and typed-value paths alike) to the
  // nearest multiple of this, in the same raw unit as min/max/defaultValue —
  // e.g. step={100} for a bps field that should move in whole-percent
  // increments. Takes precedence over integer/decimal when both are passed;
  // realistically a caller sets one or the other, not both. Full-precision
  // raw values (a stray "4372 bps" instead of "43.72%" rounded to a step the
  // field actually moves in) read as noise, not precision, to a user.
  step?: number;
  integer?: boolean;
  decimal?: boolean;
}

const RangeSlider: React.FC<RangeSliderProps> = ({
  min,
  max,
  defaultValue,
  onChange,
  displayFormat,
  inputFormat,
  displayPercentage,
  step,
  integer,
  decimal,
}) => {
  const [value1, setValue1] = useState<number>(defaultValue[0]);
  const [value2, setValue2] = useState<number>(defaultValue[1]);
  const value1Ref = useRef<number>(defaultValue[0]);
  const value2Ref = useRef<number>(defaultValue[1]);
  const trackRef = useRef<HTMLDivElement>(null);
  // Seeded with defaultValue, not null — a plain click on a handle (mousedown
  // then mouseup with no movement in between) still calls onCommit, and
  // without a real starting point here that first commit always looked like
  // a change (null never equals anything) and fired onChange with the
  // unchanged range. Callers that treat onChange as "the user edited this"
  // (e.g. a filter pill refetching) would act on a no-op click.
  const lastCommittedRef = useRef<[number, number]>([defaultValue[0], defaultValue[1]]);
  const format = displayFormat || ((value: number) => value.toString());

  // Preview updates the handle position/label only (no callback), so dragging
  // stays smooth and cheap. onChange fires once on release / discrete action.
  //
  // Clamps twice, around the rounding: the pre-round clamp picks a sensible
  // position to round from, but rounding a boundary value that isn't itself
  // aligned to the active grid (step/integer/decimal/quarter-point) can push
  // it back out past that boundary — e.g. value2 sitting at 4550 with
  // step=100 rounds to 4600 — so the post-round clamp is what actually
  // guarantees value1 never exceeds value2 (and stays within min/max).
  // Without it, a drag or click near the other handle could silently invert
  // the range (value1 > value2) and hand that straight to onChange.
  const previewValue1 = (proposedValue: number) => {
    const clamped = Math.min(Math.max(proposedValue, min), value2Ref.current);
    const rounded = roundValue(clamped, { step, integer, decimal });
    const newValue = Math.min(Math.max(rounded, min), value2Ref.current);
    value1Ref.current = newValue;
    setValue1(newValue);
  };

  const previewValue2 = (proposedValue: number) => {
    const clamped = Math.max(Math.min(proposedValue, max), value1Ref.current);
    const rounded = roundValue(clamped, { step, integer, decimal });
    const newValue = Math.max(Math.min(rounded, max), value1Ref.current);
    value2Ref.current = newValue;
    setValue2(newValue);
  };

  const commit = () => {
    const next: [number, number] = [value1Ref.current, value2Ref.current];
    const prev = lastCommittedRef.current;
    // Skip redundant emits: a plain click with no drag (mousedown->mouseup
    // with no movement) still calls onCommit, a completed drag fires
    // mouseup->commit and then the bubbled track click commits again with
    // the same values, and an arrow key at a boundary re-commits the
    // clamped (unchanged) value. Only emit on a real change.
    if (prev[0] === next[0] && prev[1] === next[1]) {
      return;
    }
    lastCommittedRef.current = next;
    onChange(next);
  };

  const updateValue1 = (proposedValue: number) => {
    previewValue1(proposedValue);
    commit();
  };

  const updateValue2 = (proposedValue: number) => {
    previewValue2(proposedValue);
    commit();
  };

  const handleMinClick = () => {
    updateValue1(min);
  };

  const handleMaxClick = () => {
    updateValue2(max);
  };

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (trackRef.current) {
      const rect = trackRef.current.getBoundingClientRect();
      const rawPosition = ((e.clientX - rect.left) / rect.width) * (max - min) + min;
      // This rounding is only for picking which handle is closer, below —
      // the authoritative clamp+round happens in previewValue1/previewValue2
      // once updateValue1/updateValue2 is called with this value.
      const clickPosition = roundValue(rawPosition, { step, integer, decimal });

      if (clickPosition < value1) {
        updateValue1(clickPosition);
      } else if (clickPosition > value2) {
        updateValue2(clickPosition);
      } else {
        const distanceToValue1 = Math.abs(value1 - clickPosition);
        const distanceToValue2 = Math.abs(value2 - clickPosition);

        if (distanceToValue1 < distanceToValue2) {
          updateValue1(clickPosition);
        } else {
          updateValue2(clickPosition);
        }
      }
    }
  };

  const left = toPercent(value1, min, max);
  const width = toPercent(value2, min, max) - left;

  const suffix = displayPercentage ? '%' : '';

  return (
    <div className="flex w-full select-none flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <EditableValueDisplay
          value={format(value1)}
          onChange={updateValue1}
          displayPercentage={displayPercentage}
          step={step}
          integer={integer}
          decimal={decimal}
          inputFormat={inputFormat}
          testId="range-min-value"
        />
        <span className="font-pt-mono text-[11px] leading-none text-gray-500">to</span>
        <EditableValueDisplay
          value={format(value2)}
          onChange={updateValue2}
          displayPercentage={displayPercentage}
          testId="range-max-value"
          step={step}
          integer={integer}
          decimal={decimal}
          inputFormat={inputFormat}
        />
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="bg-transparent shrink-0 cursor-pointer border-0 p-0 font-pt-mono text-[11px] leading-none text-gray-400 transition-colors hover:text-gray-200"
          onClick={handleMinClick}
        >
          {format(min) + suffix}
        </button>
        <div className="relative h-5 flex-1" ref={trackRef} onClick={handleTrackClick}>
          <div className="absolute top-1/2 h-1.5 w-full -translate-y-1/2 rounded-full bg-gray-700" />
          <div
            className="pointer-events-none absolute top-1/2 z-[1] h-1.5 -translate-y-1/2 rounded-full bg-brand-500"
            style={{ left: `${left}%`, width: `${width}%` }}
          />
          <Handle
            value={value1}
            min={min}
            max={max}
            onPreview={previewValue1}
            onCommit={commit}
            trackRef={trackRef}
            step={step}
            integer={integer}
            decimal={decimal}
          />
          <Handle
            value={value2}
            min={min}
            max={max}
            onPreview={previewValue2}
            onCommit={commit}
            trackRef={trackRef}
            step={step}
            integer={integer}
            decimal={decimal}
          />
        </div>
        <button
          type="button"
          className="bg-transparent shrink-0 cursor-pointer border-0 p-0 font-pt-mono text-[11px] leading-none text-gray-400 transition-colors hover:text-gray-200"
          onClick={handleMaxClick}
        >
          {format(max) + suffix}
        </button>
      </div>
    </div>
  );
};

const EditableValueDisplay: React.FC<{
  value: string;
  onChange: (newValue: number) => void;
  inputFormat?: (value: string) => number;
  displayPercentage?: boolean;
  step?: number;
  integer?: boolean;
  decimal?: boolean;
  // Playwright hook for typing an exact value directly — the reliable
  // alternative to simulating a drag, which is flaky in CI (imprecise
  // pixel math, timing-sensitive mousemove sequences). See
  // playTest/tests/dealroom/carving.spec.ts's "Slider interaction is
  // deferred" comment for why drag-based E2E coverage was avoided here.
  testId?: string;
}> = ({ value, onChange, displayPercentage, step, integer, decimal, inputFormat, testId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const submitEdit = () => {
    const parsedValue = inputFormat ? inputFormat(inputValue) : parseFloat(inputValue);
    const newValue = roundValue(parsedValue, { step, integer, decimal });

    if (isNaN(newValue)) {
      cancelEdit();
    } else {
      onChange(newValue);
      setIsEditing(false);
    }
  };

  const handleValueBlur = () => {
    submitEdit();
  };

  const handleValueKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      submitEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const beginEdit = () => {
    setInputValue(value.toString());
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setInputValue(value.toString());
    setIsEditing(false);
  };

  return isEditing ? (
    <input
      className="h-[22px] w-[64px] rounded-md border border-gray-700 bg-gray-900 px-1 text-center font-pt-mono text-[12px] tabular-nums text-brand-400 focus:border-brand-500 focus:outline-none"
      type="text"
      ref={inputRef}
      value={inputValue}
      onChange={handleValueChange}
      onBlur={handleValueBlur}
      onKeyDown={handleValueKeyDown}
      onFocus={(e) => e.target.select()}
      data-testid={testId}
    />
  ) : (
    <span
      className="inline-block cursor-pointer whitespace-nowrap rounded-md px-1 font-pt-mono text-[12px] tabular-nums text-brand-400 transition-colors hover:bg-gray-800/60 hover:text-brand-300"
      onClick={() => beginEdit()}
      data-testid={testId}
    >
      {value}
      {displayPercentage ? '%' : null}
    </span>
  );
};

export default RangeSlider;
